import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import Link, { LinkProps } from "@insite/mobius/Link";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { getPolicy } from "@store/Store/Data/getPolicy";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // Store Custom Code
    canSeeHistoryPrice: getPolicy(state, "Orders.CanSeeOrderHistoryPrice"),
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Store Custom Code
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    viewOrderGridItem?: GridItemProps;
    viewOrderLink?: LinkProps;
}

/* Store Custom Code - Start */
const headingTextStyles: SmallHeadingAndTextStyles = {
    heading: {
        color: COLORS.text.light,
        css: css`
            text-transform: inherit;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["block", "none", "block", "none", null], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    text: {
        color: COLORS.text.dark,
        weight: TYPOGRAPHY.weight.bold,
    },
};
/* Store Custom Code - End */

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    /* Store Custom Code - Start */
    orderSummaryCardGridItem: {
        width: [12, 9, 12, 9, 9],
        css: css`
            padding: 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            align-items: center;
        `,
    },
    /* Store Custom Code - End */
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: {
        /* Store Custom Code - Start */
        width: 4,
        css: css`
            padding: 0 10px;
            text-align: center;
        `,
        /* Store Custom Code - End */
    },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: {
        /* Store Custom Code - Start */
        width: 4,
        css: css`
            padding: 0 10px;
            text-align: center;
        `,
        /* Store Custom Code - End */
    },
    // Store Custom Code
    viewOrderGridItem: {
        width: [12, 3, 12, 3, 3],
        css: css`
            padding: 0 10px;
            justify-content: center;
            align-self: center;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["15px", "0", "15px", "0", null], properties: ["padding-top"] }],
                    mediaOptions.min,
                )}

            > a {
                color: ${COLORS.link.base};
                font-weight: ${TYPOGRAPHY.weight.bold};
                text-decoration: underline;

                &:hover,
                &:focus {
                    text-decoration: none;
                }
            }
        `,
    },
    orderDateHeadingAndText: headingTextStyles,
    orderNumberHeadingAndText: headingTextStyles,
    statusHeadingAndText: headingTextStyles,
    webOrderNumberHeadingAndText: headingTextStyles,
    totalHeadingAndText: headingTextStyles,
    orderDateGridItem: {
        width: 4,
        css: css`
            padding: 0 20px;

            span {
                font-weight: ${TYPOGRAPHY.weight.bold};
            }
        `,
    },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // Store Custom Code
    canSeeHistoryPrice,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    /* Store Custom Code - Start */
    return (
        <>
            <GridItem className="order-summary-card-grid-item" {...styles.orderSummaryCardGridItem}>
                <GridContainer
                    className="order-summary-card-container"
                    {...styles.container}
                    data-test-selector={`orderSummaryCard_${orderNumber}`}
                >
                    <GridItem {...styles.orderDateGridItem}>
                        {orderNumber && (
                            <SmallHeadingAndText
                                heading={translate("Date")}
                                text={orderDateDisplay}
                                extendedStyles={styles.orderDateHeadingAndText}
                            />
                        )}
                    </GridItem>
                    <GridItem className="order-number-grid-item" {...styles.orderNumberGridItem}>
                        {order.erpOrderNumber && (
                            <SmallHeadingAndText
                                heading={translate("Order #")}
                                text={order.erpOrderNumber}
                                extendedStyles={styles.orderNumberHeadingAndText}
                            />
                        )}
                    </GridItem>
                    <GridItem className="total-grid-item" {...styles.totalGridItem}>
                        {order.orderGrandTotalDisplay &&
                            canSeeHistoryPrice && ( // Store Custom Code
                                <SmallHeadingAndText
                                    heading={translate("Total")}
                                    text={order.orderGrandTotalDisplay}
                                    extendedStyles={styles.totalHeadingAndText}
                                />
                            )}
                    </GridItem>
                </GridContainer>
            </GridItem>
            {orderNumber && (
                <GridItem {...styles.viewOrderGridItem}>
                    <OrderDetailPageTypeLink title={translate("View Order")} orderNumber={orderNumber} />
                </GridItem>
            )}
        </>
    );
    /* Store Custom Code - End */
};

export default connect(mapStateToProps)(OrderSummaryCard);
